"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTransition } from "react";

interface PaginationProps {
  totalPages: number;
  onPageChangeExternal?: (page: number) => void;
}

export default function PortfolioPagination({ totalPages, onPageChangeExternal }: PaginationProps) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { replace } = useRouter();
  const [isPending, startTransition] = useTransition();
  const currentPage = Number(searchParams?.get("page")) || 1;

  function handlePage(page: number) {
    if (onPageChangeExternal) {
      onPageChangeExternal(page);
      return;
    }

    const params = new URLSearchParams(searchParams || "");
    params.set("page", page.toString());
    
    startTransition(() => {
      replace(`${pathname}?${params.toString()}`, { scroll: false });
    });
  }

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  if (totalPages <= 1) return null;

  return (
    <nav
      aria-label="Portfolio pagination"
      className={`mt-16 flex flex-wrap items-center justify-center gap-2 font-label-sm text-label-sm tracking-widest transition-opacity ${isPending ? "opacity-50" : "opacity-100"}`}
    >
      <button
        className="flex items-center gap-1 px-4 py-2 text-secondary hover:text-primary transition-colors uppercase disabled:opacity-40 disabled:cursor-not-allowed group"
        disabled={currentPage === 1}
        onClick={() => handlePage(currentPage - 1)}
        type="button"
      >
        <span className="material-symbols-outlined text-[18px] group-hover:-translate-x-1 transition-transform">
          chevron_left
        </span>
        Previous
      </button>

      <div className="flex items-center border-x border-surface-variant px-4">
        {pageNumbers.map((pageNumber) => {
          const isActive = pageNumber === currentPage;

          return (
            <button
              key={pageNumber}
              aria-current={isActive ? "page" : undefined}
              className={
                isActive
                  ? "w-10 h-10 flex items-center justify-center text-primary border border-primary -mx-[1px] relative z-10"
                  : "w-10 h-10 flex items-center justify-center text-secondary hover:text-primary transition-colors"
              }
              onClick={() => handlePage(pageNumber)}
              type="button"
            >
              {pageNumber}
            </button>
          );
        })}
      </div>

      <button
        className="flex items-center gap-1 px-4 py-2 text-secondary hover:text-primary transition-colors uppercase disabled:opacity-40 disabled:cursor-not-allowed group"
        disabled={currentPage === totalPages}
        onClick={() => handlePage(currentPage + 1)}
        type="button"
      >
        Next
        <span className="material-symbols-outlined text-[18px] group-hover:translate-x-1 transition-transform">
          chevron_right
        </span>
      </button>
    </nav>
  );
}
